import { useTranslation } from 'react-i18next';
import { Header } from "./landing/Header";
import { Footer } from "./landing/Footer";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Mail, Phone, MapPin, Clock } from "lucide-react";

const Contact = () => {
  const { t } = useTranslation('common');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Handle form submission
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="bg-gradient-hero py-20">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-t1 mb-6">
              {t('landing.contact.heroTitle')}
            </h1>
            <p className="text-xl text-t1/80 max-w-3xl mx-auto">
              {t('landing.contact.heroSubtitle')}
            </p>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Contact Form */}
              <div>
                <Card>
                  <CardHeader>
                    <CardTitle>{t('landing.contact.formTitle')}</CardTitle>
                    <CardDescription>
                      {t('landing.contact.formDescription')}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="firstName">{t('landing.contact.firstName')}</Label>
                          <Input id="firstName" placeholder="John" required />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="lastName">{t('landing.contact.lastName')}</Label>
                          <Input id="lastName" placeholder="Doe" required />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="email">{t('email')}</Label>
                        <Input id="email" type="email" placeholder="john@restaurant.com" required />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="company">{t('landing.contact.company')}</Label>
                        <Input id="company" placeholder={t('landing.contact.companyPlaceholder')} />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="phone">{t('phone')}</Label>
                        <Input id="phone" type="tel" placeholder="+30 210 1234567" />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="message">{t('landing.contact.message')}</Label>
                        <Textarea
                          id="message"
                          placeholder={t('landing.contact.messagePlaceholder')}
                          rows={5}
                          required
                        />
                      </div>

                      <Button type="submit" variant="hero" className="w-full">
                        {t('landing.contact.submit')}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </div>

              {/* Contact Information */}
              <div className="space-y-8">
                <div>
                  <h2 className="text-2xl font-bold text-foreground mb-6">{t('landing.contact.infoTitle')}</h2>
                  <p className="text-muted-foreground mb-8">
                    {t('landing.contact.infoDescription')}
                  </p>
                </div>

                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-primary/10 p-3 rounded-lg">
                      <Mail className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-1">{t('email')}</h3>
                      <p className="text-muted-foreground">hello@xentrixflow.com</p>
                      <p className="text-muted-foreground">support@xentrixflow.com</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="bg-primary/10 p-3 rounded-lg">
                      <Phone className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-1">{t('phone')}</h3>
                      <p className="text-muted-foreground">+1 (555) 123-4567</p>
                      <p className="text-muted-foreground">+1 (555) 123-4568 (Support)</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="bg-primary/10 p-3 rounded-lg">
                      <MapPin className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-1">{t('landing.contact.offices')}</h3>
                      <p className="text-muted-foreground">
                        123 Tech Street<br />
                        San Francisco, CA 94105<br />
                        United States
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="bg-primary/10 p-3 rounded-lg">
                      <Clock className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-1">{t('landing.contact.hours')}</h3>
                      <p className="text-muted-foreground">
                        {t('landing.contact.hoursLine1')}<br />
                        {t('landing.contact.hoursLine2')}<br />
                        {t('landing.contact.hoursLine3')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* CTA Card */}
                <Card className="bg-gradient-subtle border-0">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-semibold text-foreground mb-2">
                      {t('landing.contact.ctaTitle')}
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      {t('landing.contact.ctaDescription')}
                    </p>
                    <Button variant="hero">
                      {t('landing.hero.scheduleDemo')}
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Contact;
